import { AscProjectMonthAllocation, getMonthName, MonthYear } from '@common/neo.tax';
import { Typography, colors } from '@ui-components/neo.tax';

import { MonthAllocationBarForAscProject } from './MonthAllocationBarForAscProject';
import { MonthAllocationBarForMonth } from './MonthAllocationBarForMonth';
import {
  MonthAllocations,
  MonthAllocationsContainer,
  StyledStack,
  Divider,
  StyledTypography,
  OverallContainer,
} from './per-month-allocation-summary.styled';

export interface PerMonthAllocationSummaryInfo {
  selectedAscProjectAllocationPercent?: number;
  selectedAscProjectName?: string;
  monthYearAllocationsInPeriod: AscProjectMonthAllocation[][];
  periodAllocation: {
    ascProjectId: string;
    allocationPercent: number;
    useAllocationPercent: number;
  }[];
}

interface PerMonthAllocationSummaryProps extends PerMonthAllocationSummaryInfo {
  disabled: boolean;
}

export const PerMonthAllocationSummary = ({
  disabled,
  monthYearAllocationsInPeriod,
  periodAllocation,
  selectedAscProjectAllocationPercent,
  selectedAscProjectName,
}: PerMonthAllocationSummaryProps): React.ReactElement | null => {
  if (periodAllocation.length === 0) {
    return null;
  }

  const months: MonthYear[] | undefined = monthYearAllocationsInPeriod[0];

  // aggregate all allocations per month
  const getMonthYearKey = (monthYear: MonthYear): string => `${monthYear.month}-${monthYear.year}`;
  const monthYearKeyToPma = new Map<string, AscProjectMonthAllocation[]>();
  monthYearAllocationsInPeriod.flat().forEach((pma) => {
    const key = getMonthYearKey(pma);
    const pmas = monthYearKeyToPma.get(key) ?? [];
    pmas.push(pma);
    monthYearKeyToPma.set(key, pmas);
  });

  return (
    <StyledStack direction="column" paddingY={11} paddingX={6} backgroundColor={colors.gray[100]} disabled={disabled}>
      {/* TODO reuse pill component from payroll-QRE side-drawer */}
      <MonthAllocations direction="column" gap={1}>
        {!!selectedAscProjectName && !!selectedAscProjectAllocationPercent && (
          <StyledTypography color={colors.primary[600]} textAlign="center" fontSize="sm" fontWeight="semibold">
            {Math.round(selectedAscProjectAllocationPercent)}%{' '}
            <Typography color="inherit" fontSize="inherit" fontWeight="normal">
              {selectedAscProjectName}
            </Typography>
          </StyledTypography>
        )}
        <OverallContainer direction="row" alignItems="center" justifyContent="space-between">
          <Typography color={colors.black} fontWeight="medium" fontSize="sm">
            Overall
          </Typography>
          <MonthAllocationBarForAscProject width="lg" allocation={periodAllocation} />
        </OverallContainer>
      </MonthAllocations>

      <Divider />

      <MonthAllocationsContainer as="ul" direction="column">
        {months?.map(({ month, year }) => {
          const monthYearKey = getMonthYearKey({ month, year });
          const pmas = monthYearKeyToPma.get(monthYearKey) ?? [];
          return (
            <MonthAllocations
              key={`${month}-${year}`}
              as="li"
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography color={colors.gray[500]} fontSize="sm">
                {`${getMonthName(month)} ${year}`}
              </Typography>
              <MonthAllocationBarForMonth width="sm" allocation={pmas} />
            </MonthAllocations>
          );
        })}
      </MonthAllocationsContainer>
    </StyledStack>
  );
};
